import React, { useMemo, useState } from 'react'
import {
  Box,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Tooltip,
  Typography,
} from '@mui/material'

export interface StatColumn<T> {
  key: string
  label: string
  /** Explains how the number is counted */
  help?: string
  align?: 'left' | 'right'
  value: (row: T) => number | string | null
  format?: (value: number | null, row: T) => string
}

interface StatTableProps<T> {
  title?: string
  subtitle?: string
  rows: T[]
  columns: StatColumn<T>[]
  rowKey: (row: T) => string
  loading?: boolean
  empty?: string
  initialSort?: string
  onRowClick?: (row: T) => void
  maxRows?: number
}

const defaultFormat = (v: number | null) => (v === null ? '-' : v.toLocaleString('da-DK'))

/** A sortable table for one breakdown of the statistics. */
export default function StatTable<T>({
  title,
  subtitle,
  rows,
  columns,
  rowKey,
  loading = false,
  empty = 'Ingen data i perioden',
  initialSort,
  onRowClick,
  maxRows,
}: StatTableProps<T>) {
  const [sortKey, setSortKey] = useState<string | undefined>(initialSort)
  const [desc, setDesc] = useState(true)

  const sorted = useMemo(() => {
    const col = columns.find((c) => c.key === sortKey)
    if (!col) return rows
    return [...rows].sort((a, b) => {
      const av = col.value(a)
      const bv = col.value(b)
      const an = typeof av === 'number' ? av : av === null ? -Infinity : NaN
      const bn = typeof bv === 'number' ? bv : bv === null ? -Infinity : NaN
      const cmp = !Number.isNaN(an) && !Number.isNaN(bn) ? an - bn : String(av ?? '').localeCompare(String(bv ?? ''), 'da')
      return desc ? -cmp : cmp
    })
  }, [rows, columns, sortKey, desc])

  const shown = maxRows ? sorted.slice(0, maxRows) : sorted

  return (
    <Box>
      {title && (
        <Typography variant="h6" fontWeight={600} sx={{ mb: subtitle ? 0.5 : 2 }}>
          {title}
        </Typography>
      )}
      {subtitle && (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {subtitle}
        </Typography>
      )}
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress size={28} />
        </Box>
      ) : shown.length === 0 ? (
        <Box sx={{ py: 3, textAlign: 'center', backgroundColor: 'background.default', borderRadius: 2 }}>
          <Typography color="text.secondary" variant="body2">
            {empty}
          </Typography>
        </Box>
      ) : (
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                {columns.map((c) => (
                  <TableCell key={c.key} align={c.align ?? 'right'} sx={{ fontWeight: 600, whiteSpace: 'nowrap' }}>
                    <Tooltip title={c.help ?? ''} disableHoverListener={!c.help} placement="top">
                      <TableSortLabel
                        active={sortKey === c.key}
                        direction={sortKey === c.key && !desc ? 'asc' : 'desc'}
                        onClick={() => {
                          if (sortKey === c.key) setDesc(!desc)
                          else {
                            setSortKey(c.key)
                            setDesc(true)
                          }
                        }}
                      >
                        {c.label}
                      </TableSortLabel>
                    </Tooltip>
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {shown.map((row) => (
                <TableRow
                  key={rowKey(row)}
                  hover
                  onClick={onRowClick ? () => onRowClick(row) : undefined}
                  sx={onRowClick ? { cursor: 'pointer' } : undefined}
                >
                  {columns.map((c) => {
                    const v = c.value(row)
                    const text =
                      typeof v === 'number' || v === null
                        ? (c.format ?? defaultFormat)(v, row)
                        : v
                    return (
                      <TableCell key={c.key} align={c.align ?? 'right'} sx={{ fontVariantNumeric: 'tabular-nums' }}>
                        {text}
                      </TableCell>
                    )
                  })}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  )
}
